import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/common';
import { LanguageSwitcher } from '../components/common/LanguageSwitcher';
import { ProfileMenu } from '../components/common/ProfileMenu';
import { NotificationBell } from '../components/notifications/NotificationBell';
import { cn } from '../utils';
import {
  Home, Users, Calendar, Award, Gift, Trophy, Shield, Settings,
  LogOut, Menu, AlertTriangle, Star, Target, User,
  BarChart3, Dumbbell, ClipboardList, Activity, MessageCircle,
  CalendarDays, Mail, Bell, Users2, Zap, Flame,
  TrendingUp, Sparkles, Heart, ChevronRight, Building2
} from 'lucide-react';

/**
 * MainLayout — Performance Club dark theme throughout.
 *
 * Sidebar nav is grouped by category so the menu reads like a
 * table of contents rather than a wall of icons:
 *
 *   Admin    : Operate / Performance / Engage / Manage
 *   Trainer  : Operate / Performance / Engage
 *   Client   : Train / Performance / Engage / Insights
 *
 * Each section has a small uppercase kicker (10px, letter-spaced,
 * muted) that doubles as a visual divider. The mobile bottom nav
 * still uses the flat 5-item list (no categories — too cramped).
 */
export const MainLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navStructure = {
    // Master admin sees everything an org admin sees, plus the Organizations
    // (cross-gym) page. Mirrors the admin nav intentionally — the BE auth
    // helpers already accept master_admin wherever admin is allowed.
    master_admin: [
      {
        key: 'operate',
        label: t('nav.cat.operate', { defaultValue: 'Operate' }),
        items: [
          { icon: Home, label: t('nav.dashboard'), path: '/dashboard' },
          { icon: Users, label: t('nav.users'), path: '/users' },
          { icon: Calendar, label: t('nav.sessions'), path: '/sessions' },
          { icon: Dumbbell, label: t('nav.exercises'), path: '/exercises' },
          { icon: ClipboardList, label: t('nav.templates'), path: '/templates' },
          { icon: CalendarDays, label: t('nav.schedule'), path: '/schedule' },
        ],
      },
      {
        key: 'performance',
        label: t('nav.cat.performance', { defaultValue: 'Performance' }),
        items: [
          { icon: BarChart3, label: t('nav.analytics'), path: '/analytics' },
          { icon: Flame, label: t('nav.muscles', { defaultValue: 'Muscle map' }), path: '/insights/muscles' },
          { icon: Sparkles, label: t('nav.insights', { defaultValue: 'Insights' }), path: '/insights' },
        ],
      },
      {
        key: 'engage',
        label: t('nav.cat.engage', { defaultValue: 'Engage' }),
        items: [
          { icon: MessageCircle, label: t('nav.feed'), path: '/feed' },
          { icon: Mail, label: t('nav.messages'), path: '/messages' },
          { icon: Bell, label: t('nav.notifications'), path: '/notifications' },
        ],
      },
      {
        key: 'manage',
        label: t('nav.cat.manage', { defaultValue: 'Manage' }),
        items: [
          { icon: Settings, label: t('nav.gamification'), path: '/gamification' },
          { icon: Gift, label: t('nav.rewards'), path: '/rewards' },
          { icon: Target, label: t('nav.challenges'), path: '/challenges' },
          { icon: Trophy, label: t('nav.leaderboard'), path: '/leaderboard' },
          { icon: AlertTriangle, label: t('nav.flags'), path: '/flags' },
          { icon: Shield, label: t('nav.integrity'), path: '/integrity' },
          // Master admin only: see/manage every gym on the platform.
          { icon: Building2, label: t('nav.organizations'), path: '/organizations' },
        ],
      },
    ],
    admin: [
      {
        key: 'operate',
        label: t('nav.cat.operate', { defaultValue: 'Operate' }),
        items: [
          { icon: Home, label: t('nav.dashboard'), path: '/dashboard' },
          { icon: Users, label: t('nav.users'), path: '/users' },
          { icon: Calendar, label: t('nav.sessions'), path: '/sessions' },
          { icon: Dumbbell, label: t('nav.exercises'), path: '/exercises' },
          { icon: ClipboardList, label: t('nav.templates'), path: '/templates' },
          { icon: CalendarDays, label: t('nav.schedule'), path: '/schedule' },
        ],
      },
      {
        key: 'performance',
        label: t('nav.cat.performance', { defaultValue: 'Performance' }),
        items: [
          { icon: BarChart3, label: t('nav.analytics'), path: '/analytics' },
          { icon: Flame, label: t('nav.muscles', { defaultValue: 'Muscle map' }), path: '/insights/muscles' },
          { icon: Sparkles, label: t('nav.insights', { defaultValue: 'Insights' }), path: '/insights' },
        ],
      },
      {
        key: 'engage',
        label: t('nav.cat.engage', { defaultValue: 'Engage' }),
        items: [
          { icon: MessageCircle, label: t('nav.feed'), path: '/feed' },
          { icon: Mail, label: t('nav.messages'), path: '/messages' },
          { icon: Bell, label: t('nav.notifications'), path: '/notifications' },
        ],
      },
      {
        key: 'manage',
        label: t('nav.cat.manage', { defaultValue: 'Manage' }),
        items: [
          { icon: Settings, label: t('nav.gamification'), path: '/gamification' },
          { icon: Gift, label: t('nav.rewards'), path: '/rewards' },
          { icon: Target, label: t('nav.challenges'), path: '/challenges' },
          { icon: Trophy, label: t('nav.leaderboard'), path: '/leaderboard' },
          { icon: AlertTriangle, label: t('nav.flags'), path: '/flags' },
          { icon: Shield, label: t('nav.integrity'), path: '/integrity' },
          // Master-admin-only: see/manage all gyms (gated by route; safe to show in nav)
          { icon: Building2, label: t('nav.organizations'), path: '/organizations', masterOnly: true },
        ],
      },
    ],
    trainer: [
      {
        key: 'operate',
        label: t('nav.cat.operate', { defaultValue: 'Operate' }),
        items: [
          { icon: Home, label: t('nav.dashboard'), path: '/dashboard' },
          { icon: Users, label: t('nav.myClients'), path: '/clients' },
          { icon: Calendar, label: t('nav.sessions'), path: '/sessions' },
          { icon: Dumbbell, label: t('nav.exercises'), path: '/exercises' },
          { icon: ClipboardList, label: t('nav.templates'), path: '/templates' },
          { icon: Activity, label: t('nav.progress'), path: '/progress' },
        ],
      },
      {
        key: 'performance',
        label: t('nav.cat.performance', { defaultValue: 'Performance' }),
        items: [
          { icon: Flame, label: t('nav.muscles', { defaultValue: 'Muscle map' }), path: '/insights/muscles' },
          { icon: Sparkles, label: t('nav.insights', { defaultValue: 'Insights' }), path: '/insights' },
        ],
      },
      {
        key: 'engage',
        label: t('nav.cat.engage', { defaultValue: 'Engage' }),
        items: [
          { icon: MessageCircle, label: t('nav.feed'), path: '/feed' },
          { icon: Mail, label: t('nav.messages'), path: '/messages' },
          { icon: Trophy, label: t('nav.leaderboard'), path: '/leaderboard' },
        ],
      },
    ],
    client: [
      {
        key: 'train',
        label: t('nav.cat.train', { defaultValue: 'Train' }),
        items: [
          // Dashboard is the post-login landing page. Every other role
          // (admin/master_admin/trainer) lists it at the top of their nav;
          // client used to skip it, so anyone bounced back to /dashboard
          // (e.g. ProtectedRoute redirect from a forbidden link) had no way
          // to navigate. Put it back.
          { icon: Home, label: t('nav.dashboard'), path: '/dashboard' },
          { icon: Calendar, label: t('nav.sessions'), path: '/sessions' },
          { icon: Dumbbell, label: t('nav.exercises'), path: '/exercises' },
          // Templates: read-only for client. The page itself gates
          // create/edit/delete by `userRole === 'admin' || 'trainer'`, so
          // clients see the catalogue but no create button.
          { icon: ClipboardList, label: t('nav.templates'), path: '/templates' },
          { icon: CalendarDays, label: t('nav.schedule'), path: '/schedule' },
          { icon: Activity, label: t('nav.workoutLog'), path: '/workout-log' },
          { icon: TrendingUp, label: t('nav.progress'), path: '/progress' },
          { icon: Target, label: t('nav.goals', { defaultValue: 'Goals' }), path: '/progress/goals' },
        ],
      },
      {
        key: 'performance',
        label: t('nav.cat.performance', { defaultValue: 'Performance' }),
        items: [
          { icon: Trophy, label: t('nav.prs'), path: '/prs' },
          { icon: Star, label: t('nav.points'), path: '/points' },
          { icon: Award, label: t('nav.badges'), path: '/badges' },
          { icon: Trophy, label: t('nav.leaderboard', { defaultValue: 'Leaderboard' }), path: '/leaderboard' },
        ],
      },
      {
        key: 'engage',
        label: t('nav.cat.engage', { defaultValue: 'Engage' }),
        items: [
          { icon: Heart, label: t('nav.feed'), path: '/feed' },
          { icon: Mail, label: t('nav.messages'), path: '/messages' },
          { icon: Bell, label: t('nav.notifications'), path: '/notifications' },
          { icon: Users2, label: t('nav.myTeam'), path: '/team' },
          { icon: Target, label: t('nav.challenges'), path: '/challenges' },
        ],
      },
      {
        key: 'insights',
        label: t('nav.cat.insights', { defaultValue: 'Insights' }),
        items: [
          { icon: Flame, label: t('nav.muscles', { defaultValue: 'Muscle map' }), path: '/insights/muscles' },
          { icon: Sparkles, label: t('nav.insights', { defaultValue: 'Insights' }), path: '/insights' },
          { icon: Gift, label: t('nav.rewards'), path: '/rewards' },
        ],
      },
    ],
  };

  const sections = navStructure[user?.role] || [];
  const isMasterAdmin = user?.role === 'master_admin';
  // Flat list for mobile bottom nav — pick first 5 across all sections
  const flatItems = sections.flatMap((s) => s.items);
  const mobileNavItems = flatItems.slice(0, 5);

  return (
    <div className="min-h-screen" style={{ background: '#060b0a' }}>
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 w-64 border-r border-[var(--color-border)] transform transition-transform duration-200 lg:translate-x-0 flex flex-col',
          'bg-[rgba(6,12,10,0.85)] backdrop-blur-xl',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        {/* Brand */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-[var(--color-border)] shrink-0">
          <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-primary-300 rounded-xl flex items-center justify-center">
            <Zap className="w-5 h-5 text-[#0a1208]" />
          </div>
          <div>
            <h1 className="font-display text-base uppercase tracking-[0.12em] text-[var(--color-text)]">
              GymGamify
            </h1>
            <p className="text-[10px] uppercase tracking-[0.22em] text-[var(--color-text-muted)]">
              {roleLabel(user?.role, t)}
            </p>
          </div>
        </div>

        {/* Nav — grouped by category */}
        <nav
          className="flex-1 px-3 py-3 overflow-y-auto"
          data-testid="sidebar-nav"
        >
          {sections.map((section, sectionIdx) => (
            <div
              key={section.key}
              className={sectionIdx === 0 ? 'mb-2' : 'mt-5 mb-2'}
              data-testid={`nav-section-${section.key}`}
            >
              <div className="flex items-center gap-2 px-3 mb-1.5">
                <span className="text-[10px] uppercase tracking-[0.22em] text-[var(--color-text-muted)] font-semibold">
                  {section.label}
                </span>
                <div className="flex-1 h-px bg-[var(--color-border)]" />
              </div>
              <ul className="space-y-0.5">
                {section.items.filter((item) => !item.masterOnly || isMasterAdmin).map((item) => {
                  const active = location.pathname === item.path;
                  return (
                    <li key={item.path}>
                      <Link
                        to={item.path}
                        className={cn(
                          'flex items-center gap-3 px-3 py-2 rounded-xl transition-colors text-sm',
                          active
                            ? 'bg-primary-200/10 text-[var(--color-text)] border border-primary-200/30 font-medium'
                            : 'text-[var(--color-text-muted)] hover:text-[var(--color-text)] hover:bg-white/[0.04] border border-transparent'
                        )}
                        onClick={() => setSidebarOpen(false)}
                        data-testid={`nav-link-${item.path}`}
                      >
                        <item.icon
                          className={cn(
                            'w-4 h-4 shrink-0',
                            active ? 'text-primary-200' : 'text-[var(--color-text-muted)]'
                          )}
                        />
                        <span className="flex-1 truncate">{item.label}</span>
                        {active && <ChevronRight className="w-3 h-3 text-primary-200" />}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </nav>

        {/* Footer — language + profile menu (popover w/ logout + preferences) */}
        <div className="border-t border-[var(--color-border)] p-3 shrink-0">
          <div className="mb-2">
            <LanguageSwitcher variant="buttons" />
          </div>
          <ProfileMenu />
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64 pb-16 lg:pb-0">
        {/* Desktop top bar — bell + actions, sticky */}
        <header
          className="hidden lg:flex sticky top-0 z-30 h-16 items-center justify-end gap-3 px-6 border-b border-[var(--color-border)] bg-[rgba(6,12,10,0.66)] backdrop-blur-xl"
          data-testid="top-bar"
        >
          <NotificationBell />
        </header>

        {/* Mobile header */}
        <header className="lg:hidden sticky top-0 z-30 bg-[rgba(6,12,10,0.85)] backdrop-blur-xl border-b border-[var(--color-border)] px-4 py-3">
          <div className="flex items-center justify-between">
            <button onClick={() => setSidebarOpen(true)} className="p-2 -ml-2 text-[var(--color-text)]" aria-label="Open menu">
              <Menu className="w-6 h-6" />
            </button>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-primary-300 rounded-lg flex items-center justify-center">
                <Zap className="w-4 h-4 text-[#0a1208]" />
              </div>
              <h1 className="font-display uppercase tracking-[0.12em] text-[var(--color-text)]">GymGamify</h1>
            </div>
            <NotificationBell />
          </div>
        </header>

        <main className="p-4 lg:p-6">{children}</main>
      </div>

      {/* Mobile bottom navigation */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-30 bg-[rgba(6,12,10,0.92)] backdrop-blur-xl border-t border-[var(--color-border)] safe-area-pb">
        <div className="flex justify-around items-center h-16">
          {mobileNavItems.map((item) => {
            const active = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={cn(
                  'flex flex-col items-center justify-center flex-1 h-full transition-colors',
                  active ? 'text-primary-200' : 'text-[var(--color-text-muted)]'
                )}
              >
                <item.icon className="w-5 h-5" />
                <span className="text-[10px] mt-1 truncate max-w-[60px]">{item.label}</span>
              </Link>
            );
          })}
        </div>
      </nav>
    </div>
  );
};

function roleLabel(role, t) {
  if (!role) return t('nav.portal', { defaultValue: 'Portal' });
  if (role === 'admin') return t('nav.roleAdmin', { defaultValue: 'Admin Portal' });
  if (role === 'trainer') return t('nav.roleTrainer', { defaultValue: 'Trainer Portal' });
  if (role === 'client') return t('nav.roleClient', { defaultValue: 'Member Portal' });
  return `${role} ${t('nav.portal', { defaultValue: 'Portal' })}`;
}

export default MainLayout;
